import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import Skeleton from 'react-loading-skeleton'
import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import {
    Card,
    CardBody,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Table,
    UncontrolledDropdown,
    UncontrolledTooltip,
} from 'reactstrap'
import { fetchContactsThunk } from 'slices/creators/thunk'
import { AppDispatch, RootState } from 'store'
import {
    Contact,
    CreatorContactsFilters,
    CreatorsListTab,
    CreatorSortOptionLabel,
    CreatorSortOptions,
    defaultCreatorContactsFilters,
} from 'types/creators'
import { copyToClipboard, formatNumber, generateUniqueKey, getSocialAccountProfileURL } from 'utils/helpers'
import useDocumentTitle from 'hooks/useDocumentTitle'
import userDummyImg from 'assets/images/users/user-dummy-img.jpg'
import './style.scss'
import {
    toggleAddCreatorContactModal,
    toggleAddContactMobileModal,
    toggleHandleCreatorTagsModal,
    toggleManageCreatorTagsOffcanvas,
    toggleUpdateCreatorProfileModal,
} from 'slices/ui/reducer'
import CreatorFiltersModal from 'Components/creator/CreatorFiltersModal'
import BulkUploadCreatorContactModal from 'Components/creator/BulkUploadCreatorContactModal'
import { resetCreatorsState } from 'slices/creators/reducer'

const SEARCH_DEBOUNCE_MS = 400
const MIN_SEARCH_LENGTH = 3
const BULK_POLL_INTERVAL_MS = 5000

const getFiltersForTab = (
    tab: CreatorsListTab,
    baseFilters: CreatorContactsFilters
): CreatorContactsFilters => {
    const next: CreatorContactsFilters = { ...baseFilters, page: 1 }
    delete next.unprocessed
    delete next.has_error
    if (tab === 'unprocessed') next.unprocessed = 'y'
    if (tab === 'failed') next.has_error = 'y'
    return next
}

const stripTabFilters = (filters: CreatorContactsFilters): CreatorContactsFilters => {
    const next = { ...filters }
    delete next.unprocessed
    delete next.has_error
    return next
}

const platformLabel = (platform?: string) => {
    if (platform === 'ig') return 'Instagram'
    if (platform === 'yt') return 'YouTube'
    return platform ?? '—'
}

const platformIconClass = (platform?: string) => {
    if (platform === 'ig') return 'ri-instagram-line creators-platform-icon creators-platform-icon--ig'
    if (platform === 'yt') return 'ri-youtube-line creators-platform-icon creators-platform-icon--yt'
    return 'ri-global-line creators-platform-icon'
}

const formatHandle = (handle?: string) => {
    if (!handle) return '—'
    return handle.startsWith('@') ? handle : `@${handle}`
}

const PROFILE_COMPLETE = 100
const PROFILE_RING_RADIUS = 22
const PROFILE_RING_CIRCUMFERENCE = 2 * Math.PI * PROFILE_RING_RADIUS

const getProfileCompletionTone = (_completion: number) => 'low'

const CreatorProfileRing: React.FC<{ completion: number }> = ({ completion }) => {
    const tone = getProfileCompletionTone(completion)
    const ringOffset = PROFILE_RING_CIRCUMFERENCE * (1 - completion / 100)

    return (
        <svg
            className={`creators-profile-ring creators-profile-ring--${tone}`}
            viewBox="0 0 48 48"
            aria-hidden
        >
            <circle className="creators-profile-ring__track" cx="24" cy="24" r={PROFILE_RING_RADIUS} />
            <circle
                className="creators-profile-ring__progress"
                cx="24"
                cy="24"
                r={PROFILE_RING_RADIUS}
                strokeDasharray={PROFILE_RING_CIRCUMFERENCE}
                strokeDashoffset={ringOffset}
            />
        </svg>
    )
}

const CreatorRow: React.FC<{
    contact: Contact
    onUpdateProfile: (contact: Contact) => void
    onEditTags: (contact: Contact) => void
    onAddMobile: (contact: Contact) => void
}> = ({
    contact,
    onUpdateProfile,
    onEditTags,
    onAddMobile,
}) => {
        const socialAccount = contact.social_account
        const platform = socialAccount?.platform ?? contact.platform
        const mobile = contact.mobile
        const avatar = socialAccount?.profile_picture_url.split("=")[0] || userDummyImg
        const name = socialAccount?.name ?? contact.influencer?.name ?? '—'
        const handle = socialAccount?.handle ?? contact.handle
        const profileCompletion = contact.influencer?.profile_completion ?? 0
        const isProfileIncomplete =
            contact.influencer != null && profileCompletion < PROFILE_COMPLETE
        const profileTone = getProfileCompletionTone(profileCompletion)
        const followersCount = formatNumber(socialAccount?.followers_count ?? 0)

        const openProfileEditor = () => onUpdateProfile(contact)
        const profileTooltipId = `creator-profile-tip-${contact.influencer?.uuid ?? contact.id}`

        return (
            <tr className={isProfileIncomplete ? 'creators-row--incomplete' : undefined}>
                <td>
                    <div className="d-flex align-items-center gap-1">
                        <button
                            id={isProfileIncomplete ? profileTooltipId : undefined}
                            type="button"
                            className={`unset-all creators-avatar-wrap${isProfileIncomplete ? ` creators-avatar-wrap--incomplete creators-avatar-wrap--${profileTone}` : ''
                                }`}
                            onClick={isProfileIncomplete ? openProfileEditor : undefined}
                            disabled={!isProfileIncomplete}
                            aria-label={
                                isProfileIncomplete
                                    ? `Profile ${profileCompletion}% complete. Open profile editor for ${name}.`
                                    : undefined
                            }
                            tabIndex={isProfileIncomplete ? 0 : -1}
                        >
                            {isProfileIncomplete && (
                                <CreatorProfileRing completion={profileCompletion} />
                            )}
                            <img
                                src={avatar}
                                alt={name}
                                className="rounded-circle creators-avatar"
                                width={40}
                                height={40}
                                onError={(e) => {
                                    e.currentTarget.src = userDummyImg
                                }}
                            />
                            <span className="creators-platform-badge">
                                <i
                                    className={platformIconClass(platform)}
                                    aria-hidden
                                />
                            </span>
                        </button>
                        {isProfileIncomplete && (
                            <UncontrolledTooltip placement="top" target={profileTooltipId}>
                                Please complete this creator&apos;s profile ({profileCompletion}% done).
                                Click to continue.
                            </UncontrolledTooltip>
                        )}
                        <div className="d-flex flex-column gap-0 min-w-0">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="text-dark fs-14">{name}</span>

                            </div>
                            <div className="d-flex align-items-center gap-1 mt-n1">
                                <span className="text-muted fs-12">{formatHandle(handle)}</span>
                                <a
                                    href={getSocialAccountProfileURL(handle, platform)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <i className="ri-external-link-line fs-12" />
                                </a>
                            </div>
                            {isProfileIncomplete && (
                                <span className='text-primary fs-10 cursor-pointer text-decoration-underline' onClick={openProfileEditor}>Complete Profile</span>
                            )}
                        </div>
                    </div>
                </td>
                <td className="fw-medium"> <i className="ri-group-line fs-12" /> {followersCount}</td>
                <td className="text-medium"> <i className="ri-eye-line fs-12" /> {formatNumber(socialAccount?.average_views ?? 0)}</td>
                <td className="fw-medium"> <i className="ri-pulse-line fs-12" /> {socialAccount?.engagement_score ?? '—'}</td>
                <td>
                    <div className="d-flex align-items-center gap-2">
                        {mobile ? (
                            <>
                                <span className="text-dark" style={{ width: '100px' }}>
                                    <i className="ri-phone-line fs-12" /> {mobile}
                                </span>
                                <div className="d-flex align-items-center gap-2">
                                    <span onClick={() => copyToClipboard(mobile, 'Mobile number copied to your clipboard!')} className='cursor-pointer'>
                                        <button className='unset-all'>
                                            <i className="ri-file-copy-line text-primary" />
                                        </button>
                                    </span>
                                    <a href={`https://wa.me/91${mobile}`} target='_blank' rel='noopener noreferrer'>
                                        <button className='unset-all'>
                                            <i className="ri-whatsapp-line text-success "></i>
                                        </button>
                                    </a>
                                </div>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="unset-all text-primary fs-13 text-decoration-underline"
                                onClick={() => onAddMobile(contact)}
                            >
                                Add Mobile
                            </button>
                        )}
                    </div>
                </td>
                <td>
                    <span>{contact.tags?.length ? contact.tags.join(', ') : '—'}</span>
                    {' '}
                    <button
                        type="button"
                        className="unset-all text-primary"
                        onClick={() => onEditTags(contact)}
                        aria-label="Edit creator tags"
                    >
                        <i className="ri-edit-2-fill" />
                    </button>
                </td>
            </tr>
        )
    }

const BulkStatusRow: React.FC<{ contact: Contact; showError?: boolean }> = ({ contact, showError }) => {
    const platform = contact.social_account?.platform ?? contact.platform
    const handle = contact.social_account?.handle ?? contact.handle

    return (
        <tr>
            <td>
                <div className="d-flex flex-column gap-0 min-w-0">
                    <span className="text-dark fs-14">{formatHandle(handle)}</span>
                    <span className="text-muted fs-12">{platformLabel(platform)}</span>
                </div>
            </td>
            <td>
                <span>{contact.tags?.length ? contact.tags.join(', ') : '—'}</span>
            </td>
            <td>
                <span className="text-muted fs-12">{contact.created}</span>
            </td>
            {showError && (
                <td>
                    <span className="text-danger fs-13">{contact.error ?? 'Unknown error'}</span>
                </td>
            )}
        </tr>
    )
}

const Creators = () => {
    useDocumentTitle('Creators')
    const dispatch: AppDispatch = useDispatch()
    const [activeTab, setActiveTab] = useState<CreatorsListTab>('all')
    const [baseFilters, setBaseFilters] = useState(defaultCreatorContactsFilters)
    const filters = useMemo(() => getFiltersForTab(activeTab, baseFilters), [activeTab, baseFilters])
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']))
    const [searchInput, setSearchInput] = useState(baseFilters.q ?? '')
    const [filtersModalOpen, setFiltersModalOpen] = useState(false)
    const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false)
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const creatorsData = useSelector((state: RootState) => state.creators.filtered_lists[filterId!])
    const creators = creatorsData?.list ?? []
    const total = creatorsData?.count ?? 0
    const page = creatorsData?.page ?? 1
    const has_more = creatorsData?.has_more ?? true
    const unprocessed = useSelector((state: RootState) => state.creators.unprocessed)
    const failed = useSelector((state: RootState) => state.creators.failed)

    const loading = useSelector((state: RootState) => state.creators.loading)
    const [initialLoading, setInitialLoading] = useState(true)

    const refreshList = useCallback(
        (nextFilters: CreatorContactsFilters, force = false) =>
            dispatch(fetchContactsThunk({ force, filters: nextFilters })),
        [dispatch]
    )

    useEffect(() => {
        setInitialLoading(true)
        refreshList(filters).finally(() => {
            setFilterId(generateUniqueKey(filters, ['page']))
            setInitialLoading(false)
        })
    }, [filters, refreshList])

    useEffect(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
        }

        if (unprocessed <= 0) return

        const mainFilters = stripTabFilters(baseFilters)
        pollingRef.current = setInterval(() => {
            refreshList(mainFilters, true)
            if (activeTab === 'unprocessed') {
                refreshList(getFiltersForTab('unprocessed', baseFilters), true)
            } else if (activeTab === 'failed') {
                refreshList(getFiltersForTab('failed', baseFilters), true)
            }
        }, BULK_POLL_INTERVAL_MS)

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current)
                pollingRef.current = null
            }
        }
    }, [unprocessed, activeTab, baseFilters, refreshList])

    useEffect(() => {
        if (activeTab === 'unprocessed' && unprocessed === 0) {
            setActiveTab('all')
        }
        if (activeTab === 'failed' && failed === 0) {
            setActiveTab('all')
        }
    }, [activeTab, unprocessed, failed])

    useEffect(() => {
        const timeout = setTimeout(() => {
            const trimmed = searchInput.trim()
            const q = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : undefined
            const prevQ = (baseFilters.q ?? '').trim() || undefined
            if (q === prevQ) return

            const next = { ...baseFilters, page: 1 }
            if (q) {
                next.q = q
            } else {
                delete next.q
            }
            setBaseFilters(next)
        }, SEARCH_DEBOUNCE_MS)

        return () => clearTimeout(timeout)
    }, [searchInput, baseFilters])

    const nextPage = () => {
        if (has_more) {
            const nextPageNumber = page + 1
            refreshList({ ...filters, page: nextPageNumber })
        }
    }

    const handleTabChange = (tab: CreatorsListTab) => {
        setActiveTab(tab)
    }

    const handleBulkUploadSuccess = () => {
        dispatch(resetCreatorsState())
        refreshList(stripTabFilters(baseFilters), true)
        if (activeTab === 'unprocessed') {
            refreshList(getFiltersForTab('unprocessed', baseFilters), true)
        }
    }

    const showSkeleton = initialLoading || (loading && creators.length === 0)
    const isStatusTab = activeTab === 'unprocessed' || activeTab === 'failed'
    const listSubtitle =
        activeTab === 'all'
            ? `Total Creators: ${total}`
            : activeTab === 'unprocessed'
                ? `Processing: ${total}`
                : `Failed: ${total}`

    const emptyMessage =
        activeTab === 'unprocessed'
            ? 'No creators are currently being processed'
            : activeTab === 'failed'
                ? 'No failed imports'
                : 'No creators found'

    const handleUpdateProfile = (contact: Contact) => {
        if (!contact.influencer) return
        const creatorName =
            contact.social_account?.name ??
            contact.influencer.name ??
            contact.handle
        dispatch(
            toggleUpdateCreatorProfileModal({
                influencerId: contact.influencer.uuid,
                creatorName,
            })
        )
    }

    const handleEditTags = (contact: Contact) => {
        const creatorName =
            contact.social_account?.name ??
            contact.influencer?.name ??
            contact.handle
        dispatch(
            toggleHandleCreatorTagsModal({
                socialAccountId: String(contact.social_account.uuid),
                tags: contact.tags ?? [],
                creatorName,
            })
        )
    }

    const handleAddMobile = (contact: Contact) => {
        const creatorName =
            contact.social_account?.name ??
            contact.influencer?.name ??
            contact.handle
        dispatch(
            toggleAddContactMobileModal({
                socialAccountId: contact.social_account?.uuid ?? '',
                creatorName,
            })
        )
    }

    return (
        <>
            <div className="page-content creators-page mb-3">
                <Container fluid>
                    <div className="d-flex align-items-md-center justify-content-between flex-md-row flex-column mb-4 gap-2">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h3 className="page-title m-0">Creators List</h3>
                            <button
                                className="unset-all d-flex align-items-center justify-content-center"
                                style={{
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    backgroundColor: '#f3f6f9',
                                    border: '2px solid #0069AD',
                                }}
                                onClick={() => dispatch(toggleAddCreatorContactModal(true))}
                                aria-label="Add creator"
                            >
                                <i className="bx bx-plus fw-bold fs-16 text-primary" />
                            </button>
                            <button
                                type="button"
                                className="theme-btn btn-primary-outlined btn-sm"
                                onClick={() => setBulkUploadModalOpen(true)}
                            >
                                <i className="ri-upload-2-line me-1" />
                                Bulk Upload
                            </button>
                        </div>
                        <div className="d-flex align-items-center gap-2 justify-content-end flex-wrap">
                            <button
                                type="button"
                                className="theme-btn btn-primary-outlined"
                                onClick={() => dispatch(toggleManageCreatorTagsOffcanvas(true))}
                            >
                                Manage Tags
                            </button>
                            <button
                                type="button"
                                className="btn btn-white d-flex justify-content-between align-items-center gap-1"
                                onClick={() => setFiltersModalOpen(true)}
                            >
                                <i className="ri-filter-line" />
                                <span>Filter</span>
                            </button>
                            <UncontrolledDropdown className='sort-dropdown'>
                                <DropdownToggle tag={"button"} className='btn btn-white d-flex justify-content-between align-items-center w-100'>
                                    <span>
                                        <span className='text-muted me-1'>Sort by: </span>
                                        {CreatorSortOptionLabel[baseFilters.sort as CreatorSortOptions]}
                                    </span>
                                    <i className='mdi mdi-chevron-down fs-5' />
                                </DropdownToggle>
                                <DropdownMenu className='mt-1 p-0 w-100'>
                                    {
                                        Object.values(CreatorSortOptions).map((sortOption, i) => <DropdownItem key={i} onClick={() => {
                                            setBaseFilters(
                                                { ...baseFilters, sort: sortOption as CreatorSortOptions, page: 1 }
                                            )
                                        }}>{CreatorSortOptionLabel[sortOption]}</DropdownItem>)
                                    }
                                </DropdownMenu>
                            </UncontrolledDropdown>
                            <div className="campaign-layout-search-container position-relative" style={{ borderRadius: '8px' }}>
                                <span>
                                    <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M7.66732 14.0002C11.1651 14.0002 14.0007 11.1646 14.0007 7.66683C14.0007 4.16903 11.1651 1.3335 7.66732 1.3335C4.16951 1.3335 1.33398 4.16903 1.33398 7.66683C1.33398 11.1646 4.16951 14.0002 7.66732 14.0002Z" fill="#BBD8ED" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M15 16L12 13" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    className="campaign-layout-search-input"
                                />
                                {searchInput && (
                                    <i
                                        className="ri-close-line clear-search-icon"
                                        onClick={() => setSearchInput('')}
                                        role="button"
                                        tabIndex={0}
                                        aria-label="Clear search"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') setSearchInput('')
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <Nav tabs className="nav nav-tabs nav-tabs-custom nav-success creators-page__tabs mb-3">
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === 'all' })}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleTabChange('all')}
                            >
                                All Creators
                            </NavLink>
                        </NavItem>
                        {unprocessed > 0 && (
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'unprocessed' })}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleTabChange('unprocessed')}
                                >
                                    Processing
                                    <span className="badge bg-info-subtle text-info ms-2">{unprocessed}</span>
                                </NavLink>
                            </NavItem>
                        )}
                        {failed > 0 && (
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'failed' })}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleTabChange('failed')}
                                >
                                    Failed
                                    <span className="badge bg-danger-subtle text-danger ms-2">{failed}</span>
                                </NavLink>
                            </NavItem>
                        )}
                    </Nav>

                    {unprocessed > 0 && activeTab === 'all' && (
                        <div className="creators-bulk-status alert alert-info d-flex align-items-center gap-2 py-2 px-3 mb-3">
                            <span className="spinner-border spinner-border-sm text-info" role="status" aria-hidden />
                            <span>
                                Processing {unprocessed} creator{unprocessed === 1 ? '' : 's'}. This list will update automatically.
                            </span>
                        </div>
                    )}

                    <TabContent activeTab={activeTab}>
                        <TabPane tabId={activeTab}>
                    <div className="applications-table-card-header">
                        <p className="applications-table-card-header__subtitle">
                            {listSubtitle}
                        </p>
                    </div>
                    <Card className="creators-table-card border-0 shadow-sm">
                        <CardBody className="p-0">
                            <InfiniteScroll
                                dataLength={creators.length}
                                next={nextPage}
                                hasMore={has_more}
                                loader={<></>}
                            >
                                <div className="table-responsive">
                                    <Table className="align-middle table-hover creators-table mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                {isStatusTab ? (
                                                    <>
                                                        <th scope="col">Handle</th>
                                                        <th scope="col">Tags</th>
                                                        <th scope="col">Created</th>
                                                        {activeTab === 'failed' && <th scope="col">Error</th>}
                                                    </>
                                                ) : (
                                                    <>
                                                        <th scope="col">Account</th>
                                                        <th scope="col">Followers</th>
                                                        <th scope="col">Average Views</th>
                                                        <th scope="col">Engagement Score</th>
                                                        <th scope="col">Mobile</th>
                                                        <th scope="col">Tags</th>
                                                    </>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!showSkeleton && isStatusTab &&
                                                creators.map((contact) => (
                                                    <BulkStatusRow
                                                        key={contact.id ?? `${contact.handle}-${contact.platform}`}
                                                        contact={contact}
                                                        showError={activeTab === 'failed'}
                                                    />
                                                ))}
                                            {!showSkeleton && !isStatusTab &&
                                                creators.map((contact) => (
                                                    <CreatorRow
                                                        key={contact.social_account?.uuid ?? `${contact.mobile}-${contact.handle}`}
                                                        contact={contact}
                                                        onUpdateProfile={handleUpdateProfile}
                                                        onEditTags={handleEditTags}
                                                        onAddMobile={handleAddMobile}
                                                    />
                                                ))}
                                            {showSkeleton &&
                                                Array.from({ length: 8 }).map((_, index) => (
                                                    <tr key={index}>
                                                        <td colSpan={isStatusTab ? (activeTab === 'failed' ? 4 : 3) : 6} className="py-2">
                                                            <Skeleton height={48} className="rounded-2" />
                                                        </td>
                                                    </tr>
                                                ))}
                                            {!showSkeleton && !loading && creators.length === 0 && (
                                                <tr>
                                                    <td colSpan={isStatusTab ? (activeTab === 'failed' ? 4 : 3) : 6} className="text-center text-muted py-5">
                                                        {emptyMessage}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </InfiniteScroll>
                        </CardBody>
                    </Card>
                        </TabPane>
                    </TabContent>
                </Container>
            </div>
            <CreatorFiltersModal
                isOpen={filtersModalOpen}
                onClose={() => setFiltersModalOpen(false)}
                currentFilters={baseFilters}
                onApply={(next) => setBaseFilters({ ...next, page: 1 })}
            />
            <BulkUploadCreatorContactModal
                isOpen={bulkUploadModalOpen}
                onClose={() => setBulkUploadModalOpen(false)}
                onUploadSuccess={handleBulkUploadSuccess}
            />
        </>
    )
}

export default Creators
